import {FieldMoneyInput, Typography} from '@sollapay/ui/components';
import {formatNIS, parseMoney} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import {AiFlashWrapper} from '@/features/ai-extraction';
import {generatePayoutAmount, withDebugFieldChange} from '@/features/debug-test-data';

import type {useFieldAiProps} from '@/features/ai-extraction';

const PREFIX = 'view.payouts.create.details';

export interface AmountFieldProps {
  amount: string;
  onAmountChange: (value: string) => void;
  isAuthority: boolean;
  isFlashing: (field: string) => boolean;
  fieldAi: ReturnType<typeof useFieldAiProps>;
  error: string | undefined;
  mismatchedExtractedAmount: string | undefined;
  availableBalanceNis: string;
}

/** Transfer-amount field — AI-wired for authority payees, debug-fillable otherwise, with a
 *  balance hint that swaps to a mismatch warning when the lawyer edits an AI-filled amount. */
export function AmountField({
  amount,
  onAmountChange,
  isAuthority,
  isFlashing,
  fieldAi,
  error,
  mismatchedExtractedAmount,
  availableBalanceNis
}: AmountFieldProps) {
  const {t} = useTranslation('trustAccounts');

  return (
    <div className="flex flex-col gap-1">
      <AiFlashWrapper field="amount" fieldAi={fieldAi} isFlashing={isFlashing}>
        {ai => (
          <FieldMoneyInput
            label={t(`${PREFIX}.amount`)}
            value={amount}
            onChange={val => {
              if (isAuthority) ai.onAiChange();
              onAmountChange(val);
            }}
            cornerBadge={
              isAuthority
                ? ai.aiIndicator
                : withDebugFieldChange(undefined, onAmountChange, generatePayoutAmount)
            }
            isLoading={isAuthority && ai.aiGenerating}
            error={error}
            required
          />
        )}
      </AiFlashWrapper>
      {!error && !mismatchedExtractedAmount && (
        <Typography size="xs" color="tertiary">
          {t(`${PREFIX}.availableBalance`, {
            amount: formatNIS(parseMoney(availableBalanceNis))
          })}
        </Typography>
      )}
      {!error && mismatchedExtractedAmount && (
        <Typography size="xs" className="text-fg-warning">
          {t(`${PREFIX}.amountMismatchWarning`, {
            amount: formatNIS(parseMoney(mismatchedExtractedAmount))
          })}
        </Typography>
      )}
    </div>
  );
}
